import type { ComponentProps, ReactNode } from 'react';
import { Button } from 'reactstrap';
import ButtonDeleteSvg from 'src/assets/icons/ButtonDeleteSvg';
import ButtonLoaderSvg from 'src/assets/icons/ButtonLoaderSvg';

import CloseBadge from 'src/assets/icons/CloseBadge';
import HourGlassBadge from 'src/assets/icons/HourGlassBadge';
import { SaveTagSvg } from 'src/assets/icons/SaveSvg';

const whiteTickIcon = '/svg/quotes/white-tick.svg';
const circleCutIcon = '/svg/issues/circleCutIcon.svg';

import Icon from './Icon';
import type { IconType } from './Icon/types';

interface ButtonProps {
  'data-testid'?: string;
  'data-tooltip-id'?: string;
  'data-tooltip-content'?: string;
  icon?: IconType;
  isLoading?: boolean;
  className?: string;
  label: string | ReactNode;
  disabled?: boolean;
  outline?: boolean;
  type?: ComponentProps<typeof Button>['type'];
  onClick?: ComponentProps<typeof Button>['onClick'];
  customLoader?: string;
  showLabelWithLoader?: boolean;
  customButtonType?: string;
  onKeyDown?: ComponentProps<typeof Button>['onKeyDown'];
  form?: string;
}

const loadingLibrary: {
  [key: string]: {
    buttonIcon: React.ReactNode;
    loadingSvg: React.ReactNode;
    loadingLabel: string;
    buttonLabel: string;
  };
} = {
  'user-edit': {
    buttonIcon: <CloseBadge />,
    loadingSvg: <HourGlassBadge />,
    loadingLabel: 'Saving...',
    buttonLabel: 'Save',
  },
  'role-edit': {
    buttonIcon: <SaveTagSvg stroke="#fff" />,
    loadingSvg: <HourGlassBadge />,
    loadingLabel: 'Saving...',
    buttonLabel: 'Yes, Save',
  },
  'role-edit-save': {
    buttonIcon: <SaveTagSvg stroke="#fff" />,
    loadingSvg: <HourGlassBadge />,
    loadingLabel: 'Saving...',
    buttonLabel: 'Save',
  },
};

const buttonTypes: {
  [key: string]: {
    buttonIcon: React.ReactNode;
    buttonLabel: string;
  };
} = {
  delete: {
    buttonIcon: <ButtonDeleteSvg />,
    buttonLabel: 'Delete',
  },
  approve: {
    buttonIcon: <img src={whiteTickIcon} alt="" />,
    buttonLabel: 'Approve',
  },
  saveAndApprove: {
    buttonIcon: <img src={circleCutIcon} alt="" />,
    buttonLabel: 'Save & Approve',
  },
};

const ButtonWithLoader: React.FC<ButtonProps> = (props) => {
  const {
    icon,
    isLoading = false,
    className = '',
    label = '',
    disabled = false,
    showLabelWithLoader = false,
    customLoader = '',
    form = '',
    type = undefined,
    customButtonType = '',
    ...buttonProps
  } = props;

  const renderCustomLoader = (key: string) =>
    isLoading ? (
      <>
        {loadingLibrary?.[key]?.loadingSvg} {loadingLibrary?.[key]?.loadingLabel}
      </>
    ) : (
      <>
        {loadingLibrary?.[key]?.buttonIcon} {loadingLibrary?.[key]?.buttonLabel}
      </>
    );

  const renderCustomContent = (key: string) => (
    <>
      {buttonTypes?.[key]?.buttonIcon} {buttonTypes?.[key]?.buttonLabel}
    </>
  );

  let content;

  if (customLoader) {
    content = renderCustomLoader(customLoader);
  } else if (customButtonType) {
    content = isLoading ? (
      <div data-testid="button-loader" className="ls-button-loader">
        <ButtonLoaderSvg />
      </div>
    ) : (
      renderCustomContent(customButtonType)
    );
  } else if (showLabelWithLoader) {
    content = isLoading ? (
      <>
        <div className="ls-button-loader">
          <ButtonLoaderSvg />
        </div>
        {label}
      </>
    ) : (
      label
    );
  } else if (icon) {
    content = (
      <>
        {isLoading ? (
          <div className="ls-button-loader">
            <ButtonLoaderSvg />
          </div>
        ) : (
          <Icon type={icon} size="xl" />
        )}
        {label}
      </>
    );
  } else {
    content = isLoading ? (
      <div className="ls-button-loader" data-testid="loader">
        <ButtonLoaderSvg />
      </div>
    ) : (
      label
    );
  }

  return (
    <Button
      className={`${className}`}
      type={type}
      {...buttonProps}
      disabled={isLoading || disabled}
      {...(form !== '' && { form })}
    >
      {content}
    </Button>
  );
};

export default ButtonWithLoader;
